import { useState } from 'react'
import { ArrowLeftIcon } from '../../icons'
import Button from '../../Button'
import CustomerProfileImageUpload from './items/CustomerProfileImageUpload'
import { useMutation } from 'react-query'
import ApiClient from 'data/api/api_client'
import { useNotifier } from 'react-headless-notifier'
import Notification from '../../Notification'
import { useDispatch } from 'react-redux'
import { setUser } from 'store/auth'
import useErrorHandler from 'data/hooks/useErrorHandler'
import { SubmitHandler, useForm } from 'react-hook-form'
import PreferencesModal from '../../modals/PreferencesModal'
import ModalNavControl from '../../ModalNavControl'
import Form from '../../form'
import ViewDetails from './items/ViewDetails'
import EditDetails from './items/EditDetails'
import { RestUser } from 'data/types/user'
import classNames from 'classnames'
import { RestUserInput } from 'data/types/input'
import PusherProvider from 'providers/PusherProvider'
import { getProfileImage } from 'data/helpers/users'

const CustomerProfileSection = ({
  user,
  setContents,
}: {
  user: RestUser,
  setContents: (contents: string) => void
}) => {
  const { notify } = useNotifier()
  const dispatch = useDispatch()
  const handleError = useErrorHandler()

  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  const formProps = useForm<RestUserInput>({
    defaultValues: {
      ...user,
      profile_image: undefined,
      status: true,
    },
  })

  const customerMutation = useMutation(
    (details: RestUserInput) => ApiClient.users.updateUser(user.id, { ...details, status: true }),
    {
      onSuccess: (updatedUser) => {
        notify(
          <Notification message="User updated" type="success" />,
        )
        dispatch(setUser(updatedUser))
        setIsEditing(false)
        formProps.reset({
          ...updatedUser,
          profile_image: undefined,
        })
      },
      onError: (error) => {
        handleError(error, formProps.setError)
      },
    },
  )

  const onSubmit: SubmitHandler<RestUserInput> = (data) => {
    customerMutation.mutate(data)
  }

  const labelClass = 'text-grey-900 font-bold'

  return (
    <PusherProvider>
      <div>
        <form
          className="flex flex-col justify-end text-16 gap-30"
          onSubmit={formProps.handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-10 mb-auto justify-self-start">
            <button
              type="button"
              onClick={() => setContents('main')}
              className="absolute cursor-pointer focus:outline-none focus:text-primary top-20"
            >
              <ArrowLeftIcon />
            </button>

            <Form.Label className={labelClass}>
              Profile
            </Form.Label>

            <hr className="text-grey-200 h-[1px] w-full mb-30 sm:mb-20" />

            <Form.Field
              className="flex flex-col gap-10"
              error={formProps.formState.errors.profile_image?.message}
            >
              <Form.Label className={labelClass}>Profile Image</Form.Label>
              <div className="flex flex-col items-center justify-center gap-10">
                <CustomerProfileImageUpload
                  value={getProfileImage(user)}
                  onChange={v => {
                    formProps.clearErrors('profile_image')
                    formProps.setValue('profile_image', v)
                  }}
                />
              </div>
            </Form.Field>
          </div>

          <div className="flex flex-col gap-10">
            <div className="flex justify-between mb-20">
              <p className={labelClass}>Personal Information</p>

              <ModalNavControl.Button
                text="Edit"
                onClick={() => setIsEditing(!isEditing)}
                className={classNames(
                  'font-normal underline',
                  {
                    'text-grey-700 opacity-50': isEditing,
                    'text-primary': !isEditing,
                  },
                )}
              />
            </div>

            {
              isEditing
                ? <EditDetails user={user} {...formProps} />
                : <ViewDetails user={user} />
            }

            {
              ((formProps.watch('profile_image') instanceof Blob) || isEditing)
            && <Button
              type="submit"
              className="mt-10"
              isLoading={customerMutation.isLoading}
            >
              Save
            </Button>
            }
          </div>
          <div className="flex flex-col gap-10">
            <div className={labelClass}>My Preferences</div>
            <div className="mx-20 font-normal text-grey-700">
              Let us know your tastes and preferences so that we can provide you with a more tailored experience.
            </div>
          </div>

          <Button
            className="antialiased font-bold uppercase text-18 tracking-1/2"
            onClick={() => setPreferencesModalOpen(true)}
          >
            Update My Preferences
          </Button>
        </form>

        <PreferencesModal
          isOpen={preferencesModalOpen}
          onClose={() => setPreferencesModalOpen(false)}
        />
      </div>
    </PusherProvider>
  )
}

export default CustomerProfileSection
